import React from 'react';
import { Box, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { HubModal } from '../hub/HubModal';
import { HubButton, FONT_DISPLAY } from '../clients/hub/designSystem';

/**
 * ConfirmDialog (CD-05) — the one confirm, on the HubModal frame.
 * Two intensities: standard (SheetHead title + status accent), and
 * high-stakes (`highStakes` — a paprika top ticker + paprika-tinted header
 * + "This can't be undone" kicker, paprika only on the destructive button).
 * Focus defaults to Cancel. `message` accepts a ReactNode; `busy` disables
 * the buttons and shows `busyLabel` on confirm while an async action runs.
 * API is a superset of the pre-CD-05 props, so every existing call site works.
 */
export interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  highStakes?: boolean;
  busy?: boolean;
  busyLabel?: string;
  /** Hold the confirm button until the caller's condition holds (a typed project name,
   *  a required one-line reason — BSG Projects). Cancel is never held. */
  confirmDisabled?: boolean;
  /** The HubModal width preset. Default `sm`; a confirm carrying two side-by-side cards
   *  (Delete project: Goes · Stays) uses `md`. */
  size?: 'sm' | 'md';
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  variant = 'danger', highStakes, busy, busyLabel, confirmDisabled, size = 'sm', onConfirm, onCancel,
}) => {
  const Icon = variant === 'danger' ? DeleteIcon : variant === 'warning' ? WarningAmberIcon : InfoOutlinedIcon;
  const destructive = variant === 'danger';
  const resolvedTitle = title || (destructive ? 'Confirm delete' : 'Confirm action');

  const footer = (
    <>
      <HubButton variant="ghost" onClick={onCancel} autoFocus disabled={busy}>{cancelLabel}</HubButton>
      <HubButton variant={destructive ? 'danger' : 'primary'} onClick={busy || confirmDisabled ? undefined : onConfirm} disabled={busy || confirmDisabled}>
        {busy ? (busyLabel || 'Working…') : confirmLabel}
      </HubButton>
    </>
  );

  const highStakesHeader = (
    <Box sx={{ px: 2.75, pt: 2, pb: 1.5, bgcolor: 'var(--paprika-soft)', borderBottom: '1px solid var(--rule)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon sx={{ fontSize: 20, color: 'var(--paprika)' }} />
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, letterSpacing: '-0.01em', color: 'var(--ink)' }}>
          {resolvedTitle}
        </Typography>
      </Box>
      <Typography sx={{ mt: 0.5, fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--paprika-on-soft)' }}>
        This can't be undone
      </Typography>
    </Box>
  );

  return (
    <HubModal
      open={open}
      onClose={onCancel}
      title={resolvedTitle}
      size={size}
      hideClose
      footer={footer}
      topTicker={highStakes ? 'var(--paprika)' : undefined}
      header={highStakes ? highStakesHeader : undefined}
    >
      <Box sx={{ pt: highStakes ? 1.75 : 0.5, pb: 0.5 }}>
        {typeof message === 'string'
          ? <Typography sx={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.55 }}>{message}</Typography>
          : message}
      </Box>
    </HubModal>
  );
};

export default ConfirmDialog;
